import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Badge, Select, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell, Modal
} from '../../components/UI';
import { reportsAPI, usersAPI, pipelineAPI, callLogsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineDownload, HiOutlineUser, HiOutlinePhone,
  HiOutlineClock, HiOutlineTrendingUp, HiOutlineClipboardList,
  HiOutlineEye, HiOutlineCheckCircle,
  HiOutlineExclamationCircle, HiOutlineXCircle,
  HiOutlineLocationMarker, HiOutlineLightningBolt
} from 'react-icons/hi';
import { format } from 'date-fns';
import { formatLabel } from '../../utils/formatLabel';
import { VisitProductivitySection, UserVisitProductivity, UtilisationBar, fmtMins } from '../../components/Reports/VisitProductivity';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const DailyWorkReports = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [department, setDepartment] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fetch pipeline stages for dynamic stage colors
  const { data: stagesData } = useQuery({
    queryKey: ['pipelineStagesConfig'],
    queryFn: async () => {
      try {
        const response = await pipelineAPI.getAll();
        return response.data?.data || [];
      } catch (error) {
        console.error('Pipeline stages error:', error);
        return [];
      }
    },
  });

  // Create stage color map and name map
  const stageColorMap = stagesData?.reduce((acc, stage) => {
    acc[stage.key] = stage.color || '#6B7280';
    return acc;
  }, {}) || {};

  const stageNameMap = stagesData?.reduce((acc, stage) => {
    acc[stage.key] = stage.name || stage.key;
    return acc;
  }, {}) || {};

  // Map old status values to stage keys (for backward compatibility)
  const statusToStageMap = {
    'new': 'new_lead',
    'follow_up': 'in_followup',
    'hot': 'hot',
    'warm': 'warm',
    'cold': 'cold',
    'converted': 'converted',
    'closed': 'closed',
  };

  // Get color for a lead status/stage
  const getLeadStatusColor = (status) => {
    const stageKey = statusToStageMap[status] || status;
    return stageColorMap[stageKey] || '#6B7280';
  };

  // Get display name for a lead status/stage
  const getLeadStatusName = (status) => {
    const stageKey = statusToStageMap[status] || status;
    return stageNameMap[stageKey] || status?.replace(/_/g, ' ')?.toLowerCase()?.replace(/\b\w/g, l => l.toUpperCase()) || status;
  };

  // Fetch daily work report
  const { data: reportData, isLoading } = useQuery({
    queryKey: ['dailyWorkReport', selectedDate, department],
    queryFn: async () => {
      const params = { date: selectedDate };
      if (department) params.department = department;
      const response = await reportsAPI.getDailyWorkReport(params);
      return response.data?.data || {};
    },
    // Always refetch when the page is opened so stage/status changes made on
    // the Leads pages (e.g. marking a lead Converted) show up immediately —
    // the app-wide 5-minute staleTime otherwise served a cached report.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Fetch single user report when modal opens
  const { data: userReport, isLoading: userLoading } = useQuery({
    queryKey: ['userDailyReport', selectedUserId, selectedDate],
    queryFn: async () => {
      const response = await reportsAPI.getUserDailyReport(selectedUserId, { date: selectedDate });
      return response.data?.data || {};
    },
    enabled: !!selectedUserId && showUserModal,
  });

  // Fetch departments for filter
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: 'telecaller' });
      return response.data?.data || [];
    },
  });

  // Get unique departments
  const departments = usersData
    ? [...new Set(usersData.map(u => u.department).filter(Boolean))].map(d => ({ value: d, label: d }))
    : [];

  // Fetch call status distribution for the selected date
  const { data: callStatusData } = useQuery({
    queryKey: ['callStatusDistribution', selectedDate],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getCallStatusDistribution({ startDate: selectedDate, endDate: selectedDate });
        return response.data?.data || {};
      } catch (error) {
        console.error('Call status distribution error:', error);
        return {};
      }
    },
  });

  // Fetch follow-up stats for the selected date
  const { data: followUpStatsData } = useQuery({
    queryKey: ['followUpStats', selectedDate],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getFollowUpStats({ startDate: selectedDate, endDate: selectedDate });
        return response.data?.data || {};
      } catch (error) {
        console.error('Follow-up stats error:', error);
        return {};
      }
    },
  });

  // Fetch call statistics for talk-time analysis
  const { data: callTalkTimeData } = useQuery({
    queryKey: ['callTalkTimeStats', selectedDate],
    queryFn: async () => {
      try {
        const response = await callLogsAPI.getStatistics({ startDate: selectedDate, endDate: selectedDate });
        return response.data?.data || {};
      } catch (error) {
        console.error('Call talk time stats error:', error);
        return {};
      }
    },
  });

  // Compute call status breakdown for chart
  const callStatusBreakdown = (() => {
    // The endpoint returns a ready-made [{ name, value, color }] list
    // (not_connected is split by reason); older responses were a keyed object.
    if (Array.isArray(callStatusData)) {
      return callStatusData
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value);
    }
    const raw = callStatusData || {};
    const statusLabels = {
      connected: 'Connected',
      not_connected: 'Not Connected',
      no_answer: 'No Answer',
      busy: 'Busy',
      switched_off: 'Switched Off',
      wrong_number: 'Wrong Number',
      not_reachable: 'Not Reachable',
    };
    const statusColors = {
      connected: '#22c55e',
      not_connected: '#ef4444',
      no_answer: '#f59e0b',
      busy: '#3b82f6',
      switched_off: '#8b5cf6',
      wrong_number: '#6b7280',
      not_reachable: '#ec4899',
    };
    return Object.entries(statusLabels).map(([key, label]) => ({
      name: label,
      value: raw[key] || 0,
      color: statusColors[key],
    })).filter(item => item.value > 0);
  })();

  const summary = reportData?.summary || {};
  const users = reportData?.users || [];

  const totalCallStatuses = callStatusBreakdown.reduce((sum, item) => sum + item.value, 0);

  // Compute follow-up stats
  // follow-up-stats returns `due` (still pending, scheduled on the date)
  const followUpStats = {
    ...(followUpStatsData || {}),
    pending: followUpStatsData?.pending ?? followUpStatsData?.due ?? 0,
  };
  // Day total excludes overdue — that is a running backlog, not tied to the date
  const followUpTotal = (followUpStats.pending || 0) + (followUpStats.completed || 0) + (followUpStats.cancelled || 0);
  const followUpCompletionRate = followUpTotal > 0 ? Math.round(((followUpStats.completed || 0) / followUpTotal) * 100) : 0;

  // Compute conversion & connection metrics
  const connectionRate = summary.totalCalls > 0 ? Math.round(((summary.totalConnected || 0) / summary.totalCalls) * 100) : 0;
  // Converted vs. leads actually called — "worked" also counts imported /
  // auto-assigned leads nobody touched, which drags the rate toward 0%.
  const leadsCalled = summary.leadsCalled || 0;
  const conversionRate = leadsCalled > 0 ? Math.min(100, Math.round(((summary.totalConverted || 0) / leadsCalled) * 100)) : 0;
  const notConnectedCalls = Math.max(0, (summary.totalCalls || 0) - (summary.totalConnected || 0));

  // Compute top performers (sorted by connection rate, minimum 1 call)
  const topPerformers = [...users]
    .filter(u => u.calls?.total > 0)
    .sort((a, b) => (b.calls?.connectionRate || 0) - (a.calls?.connectionRate || 0))
    .slice(0, 5);

  // Talk-time derived data
  const talkTimeStats = callTalkTimeData || {};
  const avgCallDuration = talkTimeStats.averageDuration || 0;
  const totalTalkTime = talkTimeStats.totalDuration || 0;
  const totalConnectedCalls = talkTimeStats.connected || summary.totalConnected || 0;

  // Estimated per-agent talk time: avgDuration * connected calls
  const agentTalkTimeData = (() => {
    if (!users.length || !avgCallDuration) return [];
    const maxTalkTime = Math.max(...users.map(u => (u.calls?.connected || 0) * avgCallDuration), 1);
    return users
      .map(u => {
        const connectedCalls = u.calls?.connected || 0;
        const estimatedDuration = connectedCalls * avgCallDuration;
        return {
          ...u,
          estimatedTalkTime: estimatedDuration,
          talkTimePercent: maxTalkTime > 0 ? Math.round((estimatedDuration / maxTalkTime) * 100) : 0,
        };
      })
      .sort((a, b) => b.estimatedTalkTime - a.estimatedTalkTime);
  })();

  // Client-side pagination
  const totalPages = Math.max(1, Math.ceil(users.length / pageSize));
  const safePage = Math.min(page, totalPages);

  if (safePage !== page) {
    setPage(safePage);
  }

  const paginatedUsers = users.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
);

  const handleViewUser = (userId) => {
    setSelectedUserId(userId);
    setShowUserModal(true);
  };

  const handleExport = async () => {
    try {
      toast.loading('Exporting report...');
      const response = await reportsAPI.exportDailyWork({ date: selectedDate });
      const data = response.data?.data || [];

      if (data.length === 0) {
        toast.dismiss();
        toast.error('No data to export');
        return;
      }

      // Create CSV
      const headers = Object.keys(data[0]);
      const csvRows = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `daily-work-report-${selectedDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success('Report exported successfully');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export report');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      present: 'success',
      late: 'warning',
      absent: 'danger',
      half_day: 'info',
      early_leave: 'default'
    };
    return variants[status] || 'default';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatWorkingHours = (hours) => {
    if (!hours) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Format seconds into "Xh Xm" — for compact talk-time display
  const formatDurationCompact = (seconds) => {
    if (!seconds || seconds <= 0) return '0m';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  // Format seconds into "Xm Xs" — for average call duration display
  const formatAvgDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '0s';
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Daily Work Reports</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Track daily performance of all telecallers
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none"
              max={format(new Date(), 'yyyy-MM-dd')}
            />
            {departments.length > 0 && (
              <Select
                options={[{ value: '', label: 'All Depts' }, ...departments]}
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="w-28 sm:w-32"
              />
            )}
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineDownload className="w-4 h-4" />}
              onClick={handleExport}
            >
              <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-gray-900">{summary.totalUsers || 0}</div>
            <div className="text-xs lg:text-sm text-gray-500">Telecallers</div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-green-50">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-green-600">{summary.present || 0}</div>
            <div className="text-xs lg:text-sm text-green-600">Present</div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-red-50">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-red-600">{summary.absent || 0}</div>
            <div className="text-xs lg:text-sm text-red-600">Absent</div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-yellow-50">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-yellow-600">{summary.late || 0}</div>
            <div className="text-xs lg:text-sm text-yellow-600">Late</div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-primary-50 col-span-2 sm:col-span-1">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-primary-600">{summary.totalCalls || 0}</div>
            <div className="text-xs lg:text-sm text-primary-600">Total Calls</div>
          </Card>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
          <Card padding="p-2.5 sm:p-3 lg:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <HiOutlinePhone className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{summary.totalConnected || 0}</div>
                <div className="text-xs text-gray-500">Connected</div>
              </div>
            </div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <HiOutlineClipboardList className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{summary.totalLeads ?? summary.totalLeadsWorked ?? 0}</div>
                <div className="text-xs text-gray-500">Total Leads</div>
                <div className="text-[11px] text-gray-400 truncate" title="Distinct leads called on the selected date">
                  {summary.leadsCalled || 0} {summary.leadsCalled === 1 ? 'lead' : 'leads'} called this day
                </div>
              </div>
            </div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <HiOutlineTrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{summary.totalConverted || 0}</div>
                <div className="text-xs text-gray-500">Converted this day</div>
                {summary.totalConvertedAllTime !== undefined && (
                  <div className="text-[11px] text-gray-400 truncate" title="All leads currently in a Converted (won) stage">
                    {summary.totalConvertedAllTime} converted overall
                  </div>
                )}
              </div>
            </div>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <HiOutlineClock className="w-4 h-4 lg:w-5 lg:h-5 text-orange-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{formatWorkingHours(summary.totalWorkingHours)}</div>
                <div className="text-xs text-gray-500">Hours</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Visits & Productivity (site visits + talk time vs attendance) */}
        {!isLoading && users.length > 0 && (
          <VisitProductivitySection summary={summary} users={users} onViewUser={handleViewUser} />
        )}

        {/* Daily Performance Summary */}
        {(() => {
          const hasCallData = callStatusBreakdown.length > 0;
          const hasFollowUpData = followUpTotal > 0 || (followUpStats.overdue || 0) > 0;
          const hasPerformerData = topPerformers.length > 0;
          const hasAnyData = hasCallData || hasFollowUpData || summary.totalCalls > 0;

          if (!hasAnyData && !isLoading) return null;

          return (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-center gap-2">
                <HiOutlineTrendingUp className="w-5 h-5 text-primary-600" />
                <h2 className="text-base sm:text-lg font-bold text-gray-900">Daily Performance Summary</h2>
              </div>

              {/* Conversion & Connection Metrics */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
                {[
                  {
                    label: 'Total Calls',
                    value: summary.totalCalls || 0,
                    sub: `${summary.totalConnected || 0} connected · ${notConnectedCalls} not connected`,
                    icon: HiOutlinePhone,
                    iconBg: 'bg-primary-100',
                    tone: 'text-primary-600',
                    // Stacked bar: connected share in green, rest in red
                    bar: (summary.totalCalls || 0) > 0 ? (
                      <div className="flex h-full">
                        <div className="h-full bg-green-500 transition-all" style={{ width: `${connectionRate}%` }} />
                        <div className="h-full bg-red-400 transition-all" style={{ width: `${100 - connectionRate}%` }} />
                      </div>
                    ) : null,
                  },
                  {
                    label: 'Connection Rate',
                    value: `${connectionRate}%`,
                    sub: `${summary.totalConnected || 0} of ${summary.totalCalls || 0} calls answered`,
                    icon: HiOutlineCheckCircle,
                    iconBg: 'bg-green-100',
                    tone: 'text-green-600',
                    bar: <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${connectionRate}%` }} />,
                  },
                  {
                    label: 'Conversion Rate',
                    value: `${conversionRate}%`,
                    sub: `${summary.totalConverted || 0} converted of ${leadsCalled} called`,
                    icon: HiOutlineTrendingUp,
                    iconBg: 'bg-purple-100',
                    tone: 'text-purple-600',
                    bar: <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${conversionRate}%` }} />,
                  },
                  {
                    label: 'Leads Called',
                    value: leadsCalled,
                    sub: `${followUpStats.completed || 0} follow-ups completed`,
                    icon: HiOutlineClipboardList,
                    iconBg: 'bg-blue-100',
                    tone: 'text-blue-600',
                    bar: null,
                  },
                ].map(({ label, value, sub, icon: Icon, iconBg, tone, bar }) => (
                  <Card key={label} padding="p-2.5 sm:p-3 lg:p-4">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className={`w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-4 h-4 lg:w-5 lg:h-5 ${tone}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs lg:text-sm text-gray-500">{label}</div>
                        <div className={`text-base sm:text-lg lg:text-2xl font-bold ${tone}`}>{value}</div>
                        <div className="text-[11px] text-gray-400 truncate" title={sub}>{sub}</div>
                      </div>
                    </div>
                    {bar && (
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">{bar}</div>
                    )}
                  </Card>
                ))}
              </div>

              {/* Two Column: Call Outcomes + Follow-up Stats */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Call Outcomes Breakdown */}
                <Card padding="p-3 sm:p-4 lg:p-5">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Call Outcomes Breakdown</h3>
                  {hasCallData ? (
                    <div className="space-y-2.5 sm:space-y-3">
                      {callStatusBreakdown.map((item) => {
                        const pct = totalCallStatuses > 0 ? Math.round((item.value / totalCallStatuses) * 100) : 0;
                        return (
                          <div key={item.name} className="space-y-1">
                            <div className="flex items-center justify-between text-xs sm:text-sm">
                              <span className="font-medium text-gray-700">{item.name}</span>
                              <div className="flex items-center gap-1.5">
                                <span className="text-gray-900 font-semibold">{item.value}</span>
                                <span className="text-gray-400">({pct}%)</span>
                              </div>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${pct}%`, backgroundColor: item.color }}
                              />
                            </div>
                          </div>
                        );
                      })}
                      <div className="pt-2 border-t border-gray-100 flex justify-between text-xs sm:text-sm font-semibold text-gray-900">
                        <span>Total Calls</span>
                        <span>{totalCallStatuses}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 sm:py-8">
                      <HiOutlinePhone className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
                      <p className="text-xs sm:text-sm text-gray-400">No call data available for this date</p>
                    </div>
                  )}
                </Card>

                {/* Follow-up Summary */}
                <Card padding="p-3 sm:p-4 lg:p-5">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">Follow-up Summary</h3>
                    {followUpTotal > 0 && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                        {followUpCompletionRate}% completed
                      </span>
                    )}
                  </div>
                  {hasFollowUpData ? (
                    <div className="space-y-2.5 sm:space-y-3">
                      {[
                        { key: 'completed', label: 'Completed', hint: 'Done on this date', icon: HiOutlineCheckCircle, chip: 'bg-green-100', tone: 'text-green-600', bar: 'bg-green-500' },
                        { key: 'pending', label: 'Pending', hint: 'Scheduled on this date, not done yet', icon: HiOutlineClock, chip: 'bg-yellow-100', tone: 'text-yellow-600', bar: 'bg-yellow-500' },
                        { key: 'cancelled', label: 'Cancelled', hint: 'Scheduled on this date, cancelled', icon: HiOutlineXCircle, chip: 'bg-gray-100', tone: 'text-gray-500', bar: 'bg-gray-400' },
                      ].map(({ key, label, hint, icon: Icon, chip, tone, bar }) => {
                        const count = followUpStats[key] || 0;
                        const pct = followUpTotal > 0 ? Math.round((count / followUpTotal) * 100) : 0;
                        return (
                          <div key={key} className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full ${chip} flex items-center justify-center flex-shrink-0`}>
                              <Icon className={`w-4 h-4 ${tone}`} />
                            </div>
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-center justify-between text-xs sm:text-sm">
                                <span className="font-medium text-gray-700 truncate" title={hint}>{label}</span>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className="text-gray-900 font-semibold">{count}</span>
                                  <span className="text-gray-400">({pct}%)</span>
                                </div>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div className={`h-full ${bar} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="pt-2 border-t border-gray-100 flex justify-between text-xs sm:text-sm font-semibold text-gray-900">
                        <span>Total for this date</span>
                        <span>{followUpTotal}</span>
                      </div>
                      {/* Overdue is a running backlog (as of today), not a count for the selected date */}
                      <div className={`flex items-center justify-between gap-3 p-2.5 rounded-xl ${(followUpStats.overdue || 0) > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <HiOutlineExclamationCircle className={`w-5 h-5 flex-shrink-0 ${(followUpStats.overdue || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                          <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-700">Overdue</p>
                            <p className="text-[11px] text-gray-400 truncate">Past due &amp; still pending, as of today</p>
                          </div>
                        </div>
                        <span className={`text-base sm:text-lg font-bold ${(followUpStats.overdue || 0) > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {followUpStats.overdue || 0}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 sm:py-8">
                      <HiOutlineClipboardList className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
                      <p className="text-xs sm:text-sm text-gray-400">No follow-up data available for this date</p>
                    </div>
                  )}
                </Card>
              </div>

              {/* Top Performers */}
              {hasPerformerData && (
                <Card padding="p-3 sm:p-4 lg:p-5">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Top Performers</h3>
                  {/* Desktop Table */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 pr-4">#</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 pr-4">Agent</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 pr-4">Attendance</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 pr-4">Calls</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 pr-4">Connection Rate</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2">Converted</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {topPerformers.map((user, idx) => (
                          <tr key={user.userId || idx} className="hover:bg-gray-50 transition-colors">
                            <td className="py-2.5 pr-4">
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                                idx === 1 ? 'bg-gray-100 text-gray-600' :
                                idx === 2 ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-50 text-gray-500'
                              }`}>
                                {idx + 1}
                              </span>
                            </td>
                            <td className="py-2.5 pr-4">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-primary-600 font-medium text-xs">{user.name?.split(' ').map(n => n[0]).join('')}</span>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                                  <p className="text-xs text-gray-500 truncate">{user.department}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-2.5 pr-4">
                              <Badge variant={getStatusBadge(user.attendance?.status)} size="sm">
                                {formatLabel(user.attendance?.status)}
                              </Badge>
                            </td>
                            <td className="py-2.5 pr-4">
                              <div className="text-sm">
                                <span className="font-medium text-gray-900">{user.calls?.total || 0}</span>
                                <span className="text-xs text-green-600 ml-1">({user.calls?.connected || 0} conn)</span>
                              </div>
                            </td>
                            <td className="py-2.5 pr-4">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                                  <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${user.calls?.connectionRate || 0}%` }} />
                                </div>
                                <span className="text-sm text-gray-600">{user.calls?.connectionRate || 0}%</span>
                              </div>
                            </td>
                            <td className="py-2.5">
                              <span className="text-sm font-medium text-purple-600">{user.leads?.converted || 0}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile Cards */}
                  <div className="sm:hidden space-y-3">
                    {topPerformers.map((user, idx) => (
                      <div key={user.userId || idx} className="p-3 border border-gray-100 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2.5">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                              idx === 1 ? 'bg-gray-100 text-gray-600' :
                              idx === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-50 text-gray-500'
                            }`}>
                              {idx + 1}
                            </span>
                            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-primary-600 font-medium text-xs">{user.name?.split(' ').map(n => n[0]).join('')}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                              <p className="text-xs text-gray-500 truncate">{user.department}</p>
                            </div>
                          </div>
                          <Badge variant={getStatusBadge(user.attendance?.status)} size="sm">
                            {formatLabel(user.attendance?.status)}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center text-xs">
                          <div className="bg-gray-50 rounded-lg p-1.5">
                            <div className="font-semibold text-gray-900">{user.calls?.total || 0}</div>
                            <div className="text-gray-500">Calls</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-1.5">
                            <div className="font-semibold text-green-600">{user.calls?.connectionRate || 0}%</div>
                            <div className="text-gray-500">Rate</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-1.5">
                            <div className="font-semibold text-purple-600">{user.leads?.converted || 0}</div>
                            <div className="text-gray-500">Won</div>
                          </div>
                        </div>
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${user.calls?.connectionRate || 0}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          );
        })()}

        {/* Talk-Time Analysis */}
        {(() => {
          const hasTalkTimeData = totalTalkTime > 0 || avgCallDuration > 0;
          const hasAgentData = agentTalkTimeData.length > 0;

          if (!hasTalkTimeData && !hasAgentData && !isLoading) return null;

          return (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-center gap-2">
                <HiOutlineClock className="w-5 h-5 text-primary-600" />
                <h2 className="text-base sm:text-lg font-bold text-gray-900">Talk-Time Analysis</h2>
              </div>

              {/* Talk-Time Metric Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
                <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-primary-50">
                  <div className="text-base sm:text-lg lg:text-2xl font-bold text-primary-600">
                    {formatDurationCompact(totalTalkTime)}
                  </div>
                  <div className="text-xs lg:text-sm text-gray-500">Total Talk Time</div>
                </Card>
                <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-green-50">
                  <div className="text-base sm:text-lg lg:text-2xl font-bold text-green-600">
                    {formatAvgDuration(avgCallDuration)}
                  </div>
                  <div className="text-xs lg:text-sm text-gray-500">Avg Call Duration</div>
                </Card>
                <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-purple-50">
                  <div className="text-base sm:text-lg lg:text-2xl font-bold text-purple-600">
                    {totalConnectedCalls}
                  </div>
                  <div className="text-xs lg:text-sm text-gray-500">Connected Calls</div>
                </Card>
                <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-orange-50">
                  <div className="text-base sm:text-lg lg:text-2xl font-bold text-orange-600">
                    {avgCallDuration > 0
                      ? formatDurationCompact(avgCallDuration * totalConnectedCalls)
                      : '0m'}
                  </div>
                  <div className="text-xs lg:text-sm text-gray-500">Est. Connected Time</div>
                </Card>
              </div>

              {/* Per-Agent Talk Time Breakdown */}
              {hasAgentData && (
                <Card padding="p-0">
                  <div className="p-3 sm:p-4 lg:p-5 border-b border-gray-100">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                      Agent Talk-Time Breakdown
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Estimated using avg duration ({formatAvgDuration(avgCallDuration)}) per connected call
                    </p>
                  </div>

                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHead>
                        <TableRow hoverable={false}>
                          <TableCell header className="min-w-[40px]">#</TableCell>
                          <TableCell header className="min-w-[150px]">Agent</TableCell>
                          <TableCell header className="min-w-[80px]">Connected</TableCell>
                          <TableCell header className="min-w-[100px]">Est. Talk Time</TableCell>
                          <TableCell header className="min-w-[140px]">Talk Time Comparison</TableCell>
                          <TableCell header className="min-w-[80px]">Conn. Rate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {agentTalkTimeData.map((user, idx) => (
                          <TableRow key={user.userId || idx}>
                            <TableCell>
                              <span className="text-sm text-gray-500">{idx + 1}</span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-primary-600 font-medium text-xs">
                                    {user.name?.split(' ').map(n => n[0]).join('')}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                                  <p className="text-xs text-gray-500 truncate">{user.department}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-medium text-gray-900">{user.calls?.connected || 0}</span>
                              <span className="text-xs text-gray-400 ml-1">/ {user.calls?.total || 0}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-semibold text-gray-900">
                                {formatDurationCompact(user.estimatedTalkTime)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-20 lg:w-28 h-2 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                                  <div
                                    className="h-full bg-primary-500 rounded-full transition-all"
                                    style={{ width: `${user.talkTimePercent}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500">{user.talkTimePercent}%</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-gray-600">{user.calls?.connectionRate || 0}%</span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden divide-y divide-gray-100">
                    {agentTalkTimeData.map((user, idx) => (
                      <div key={user.userId || idx} className="p-3 sm:p-4">
                        <div className="flex items-center gap-2.5 mb-2">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-600 font-medium text-xs">
                              {user.name?.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                            <p className="text-xs text-gray-500 truncate">{user.department}</p>
                          </div>
                          <span className="text-sm font-semibold text-primary-600">
                            {formatDurationCompact(user.estimatedTalkTime)}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center text-xs mb-2">
                          <div className="bg-gray-50 rounded-lg p-1.5">
                            <div className="font-semibold text-gray-900">{user.calls?.connected || 0}</div>
                            <div className="text-gray-500">Connected</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-1.5">
                            <div className="font-semibold text-green-600">{user.calls?.connectionRate || 0}%</div>
                            <div className="text-gray-500">Rate</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-1.5">
                            <div className="font-semibold text-primary-600">{formatDurationCompact(user.estimatedTalkTime)}</div>
                            <div className="text-gray-500">Talk Time</div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-full bg-primary-500 rounded-full transition-all"
                            style={{ width: `${user.talkTimePercent}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          );
        })()}

        {/* User Reports Table */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : users.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineUser className="w-8 h-8 text-gray-400" />}
              title="No reports found"
              description="No telecallers found for the selected date"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header className="min-w-[150px]">Employee</TableCell>
                    <TableCell header className="min-w-[90px]">Attendance</TableCell>
                    <TableCell header className="min-w-[80px]">Working Hours</TableCell>
                    <TableCell header className="min-w-[80px]">Calls</TableCell>
                    <TableCell header className="min-w-[120px]">Connection Rate</TableCell>
                    <TableCell header className="min-w-[80px]">Leads Worked</TableCell>
                    <TableCell header className="min-w-[80px]">Converted</TableCell>
                    <TableCell header className="min-w-[80px]">Visits</TableCell>
                    <TableCell header className="min-w-[120px]">Utilisation</TableCell>
                    <TableCell header className="min-w-[80px]">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.map((user) => (
                    <TableRow key={user.userId}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-600 font-medium text-sm">
                              {user.name?.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{user.name}</p>
                            <p className="text-xs text-gray-500 truncate">{user.department}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant={getStatusBadge(user.attendance.status)} size="sm">
                            {formatLabel(user.attendance.status)}
                          </Badge>
                          {user.attendance.isLate && (
                            <span className="text-xs text-yellow-600 whitespace-nowrap">Late</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 whitespace-nowrap">
                          {formatWorkingHours(user.attendance.workingHours)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">{user.calls.total}</span>
                          <span className="text-xs text-green-600 whitespace-nowrap">{user.calls.connected} connected</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-12 lg:w-16 h-2 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                            <div
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${user.calls.connectionRate}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 whitespace-nowrap">{user.calls.connectionRate}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-900">{user.leads.total}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-green-600 font-medium">{user.leads.converted}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900 whitespace-nowrap">
                            {user.visits?.completed || 0}
                            <span className="text-gray-400 font-normal"> / {user.visits?.scheduled || 0}</span>
                          </span>
                          <span className="text-xs text-gray-500 whitespace-nowrap">{fmtMins(user.visits?.actualMinutes || 0)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <UtilisationBar value={user.productivity?.utilisation} />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1.5"
                          icon={<HiOutlineEye className="w-4 h-4" />}
                          onClick={() => handleViewUser(user.userId)}
                          title="View Details"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {paginatedUsers.map((user) => (
                <div
                  key={user.userId}
                  className="p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors"
                  onClick={() => handleViewUser(user.userId)}
                >
                  {/* Header Row */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-600 font-semibold text-sm">
                          {user.name?.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.department}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Badge variant={getStatusBadge(user.attendance.status)} size="sm">
                        {formatLabel(user.attendance.status)}
                      </Badge>
                      {user.attendance.isLate && (
                        <span className="text-xs text-yellow-600 font-medium">Late</span>
                      )}
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-3">
                    <div className="flex items-center gap-2">
                      <HiOutlineClock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700">{formatWorkingHours(user.attendance.workingHours)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HiOutlinePhone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700">{user.calls.total} calls</span>
                      <span className="text-green-600 text-xs">({user.calls.connected} conn)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HiOutlineClipboardList className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700">{user.leads.total} leads</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HiOutlineTrendingUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-green-600 font-medium">{user.leads.converted} converted</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HiOutlineLocationMarker className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700">
                        {user.visits?.completed || 0}/{user.visits?.scheduled || 0} visits
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HiOutlineLightningBolt className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-700">
                        {user.productivity?.utilisation === null || user.productivity?.utilisation === undefined
                          ? 'No attendance'
                          : `${user.productivity.utilisation}% utilised`}
                      </span>
                    </div>
                  </div>

                  {/* Connection Rate Bar */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all duration-300"
                        style={{ width: `${user.calls.connectionRate}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-600 flex-shrink-0">{user.calls.connectionRate}%</span>
                  </div>

                  {/* View Details Indicator */}
                  <div className="flex items-center justify-end mt-2">
                    <span className="text-xs text-primary-600 font-medium flex items-center gap-1">
                      View Details
                      <HiOutlineEye className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
         {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">

          <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-500">
            <span>
              Showing {(safePage - 1) * pageSize + 1}–
              {Math.min(safePage * pageSize, users.length)} of {users.length}
            </span>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              <label
                htmlFor="rows-per-page"
                className="text-xs sm:text-sm text-gray-500 whitespace-nowrap"
              >
                Rows per page:
              </label>

              <select
                id="rows-per-page"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="px-2 py-1 border border-gray-200 rounded-md text-sm bg-white"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

            <div className="flex items-center gap-1.5 sm:gap-2">

              <Button
                variant="outline"
                size="sm"
                disabled={safePage === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </Button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => setPage(pageNumber)}
                  className={
                    safePage === pageNumber
                      ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white"
                      : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }
                >
                  {pageNumber}
                </button>
              ))}

              <Button
                variant="outline"
                size="sm"
                disabled={safePage === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>

            </div>

        </div>
          </Card>
        )}

        {/* User Detail Modal */}
        <Modal
          isOpen={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUserId(null);
          }}
          title={userReport?.user?.name || 'User Report'}
          size="lg"
        >
          {userLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : userReport ? (
            <div className="space-y-3 sm:space-y-4">
              {/* User Info - Sticky Header */}
              <div className="sticky top-0 z-20 flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-xl border-b border-gray-200 shadow-sm">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 font-medium text-base sm:text-lg">
                    {userReport.user?.name?.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{userReport.user?.name}</p>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">{userReport.user?.email}</p>
                  <p className="text-xs text-gray-400 truncate">{userReport.user?.department} - {userReport.user?.designation}</p>
                </div>
                <Badge variant={getStatusBadge(userReport.attendance?.status)} size="sm">
                  {formatLabel(userReport.attendance?.status, 'Absent')}
                </Badge>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
                <div className="text-center p-2.5 sm:p-3 bg-blue-50 rounded-xl">
                  <div className="text-base sm:text-lg lg:text-xl font-bold text-blue-600">{userReport.callStats?.total || 0}</div>
                  <div className="text-xs text-blue-600">Total Calls</div>
                </div>
                <div className="text-center p-2.5 sm:p-3 bg-green-50 rounded-xl">
                  <div className="text-base sm:text-lg lg:text-xl font-bold text-green-600">{userReport.callStats?.connected || 0}</div>
                  <div className="text-xs text-green-600">Connected</div>
                </div>
                <div className="text-center p-2.5 sm:p-3 bg-purple-50 rounded-xl">
                  <div className="text-base sm:text-lg lg:text-xl font-bold text-purple-600">{userReport.callStats?.connectionRate || 0}%</div>
                  <div className="text-xs text-purple-600">Rate</div>
                </div>
                <div className="text-center p-2.5 sm:p-3 bg-orange-50 rounded-xl">
                  <div className="text-base sm:text-lg lg:text-xl font-bold text-orange-600">{formatWorkingHours(userReport.attendance?.workingHours)}</div>
                  <div className="text-xs text-orange-600">Hours</div>
                </div>
              </div>

              {/* Talk Time Summary */}
              {avgCallDuration > 0 && (
                <div className="p-3 sm:p-4 border border-gray-200 rounded-xl">
                  <h4 className="font-medium text-gray-900 mb-2 sm:mb-3">Talk Time Summary</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    <div className="text-center p-2.5 bg-primary-50 rounded-xl">
                      <div className="text-base sm:text-lg font-bold text-primary-600">
                        {formatDurationCompact((userReport.callStats?.connected || 0) * avgCallDuration)}
                      </div>
                      <div className="text-xs text-gray-500">Est. Talk Time</div>
                    </div>
                    <div className="text-center p-2.5 bg-green-50 rounded-xl">
                      <div className="text-base sm:text-lg font-bold text-green-600">
                        {formatAvgDuration(avgCallDuration)}
                      </div>
                      <div className="text-xs text-gray-500">Avg Duration</div>
                    </div>
                    <div className="text-center p-2.5 bg-orange-50 rounded-xl col-span-2 sm:col-span-1">
                      <div className="text-base sm:text-lg font-bold text-orange-600">
                        {userReport.callStats?.connected || 0}
                      </div>
                      <div className="text-xs text-gray-500">Connected Calls</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Productivity, visits and day timeline */}
              <UserVisitProductivity report={userReport} />

              {/* Attendance Details */}
              {userReport.attendance && (
                <div className="p-3 sm:p-4 border border-gray-200 rounded-xl">
                  <h4 className="font-medium text-gray-900 mb-2 sm:mb-3">Attendance Details</h4>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                    <div>
                      <span className="text-gray-500">Check In:</span>
                      <span className="ml-1 sm:ml-2 font-medium">
                        {userReport.attendance.checkInTime
                          ? format(new Date(userReport.attendance.checkInTime), 'h:mm a')
                          : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Check Out:</span>
                      <span className="ml-1 sm:ml-2 font-medium">
                        {userReport.attendance.checkOutTime
                          ? format(new Date(userReport.attendance.checkOutTime), 'h:mm a')
                          : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <Badge variant={getStatusBadge(userReport.attendance.status)} size="sm" className="ml-1 sm:ml-2">
                        {formatLabel(userReport.attendance.status)}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-gray-500">Leads:</span>
                      <span className="ml-1 sm:ml-2 font-medium">{userReport.allocation?.leadsAllocated || 0}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Hourly Call Breakdown Chart (9 AM - 9 PM IST) */}
              {userReport.hourlyBreakdown && userReport.hourlyBreakdown.length > 0 && (
                <div className="p-3 sm:p-4 border border-gray-200 rounded-xl relative z-0 overflow-hidden">
                  <h4 className="font-medium text-gray-900 mb-2 sm:mb-3">Hourly Call Breakdown (9 AM - 9 PM IST)</h4>
                  <div className="h-48 sm:h-56 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={userReport.hourlyBreakdown.map(h => ({
                          ...h,
                          estTalkTime: avgCallDuration > 0 ? Math.round(((h.connected || 0) * avgCallDuration) / 60) : 0,
                        }))}
                        margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 10, fill: '#6b7280' }}
                          axisLine={{ stroke: '#e5e7eb' }}
                          tickLine={false}
                          interval={0}
                          angle={-45}
                          textAnchor="end"
                          height={50}
                        />
                        <YAxis
                          yAxisId="left"
                          tick={{ fontSize: 10, fill: '#6b7280' }}
                          axisLine={{ stroke: '#e5e7eb' }}
                          tickLine={false}
                          allowDecimals={false}
                        />
                        {avgCallDuration > 0 && (
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 10, fill: '#6b7280' }}
                            axisLine={{ stroke: '#e5e7eb' }}
                            tickLine={false}
                            allowDecimals={false}
                            label={{ value: 'Min', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: '#6b7280' } }}
                          />
                        )}
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            fontSize: '12px',
                            position: 'relative',
                            zIndex: 50,
                          }}
                          formatter={(value, name) => {
                            if (name === 'estTalkTime') return [`${value} min`, 'Est. Talk Time'];
                            if (name === 'total') return [value, 'Total Calls'];
                            if (name === 'connected') return [value, 'Connected'];
                            return [value, name];
                          }}
                          labelFormatter={(label) => `Time: ${label}`}
                        />
                        <Bar
                          yAxisId="left"
                          dataKey="total"
                          name="total"
                          fill="#4F46E5"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          yAxisId="left"
                          dataKey="connected"
                          name="connected"
                          fill="#22c55e"
                          radius={[4, 4, 0, 0]}
                        />
                        {avgCallDuration > 0 && (
                          <Bar
                            yAxisId="right"
                            dataKey="estTalkTime"
                            name="estTalkTime"
                            fill="#f59e0b"
                            radius={[4, 4, 0, 0]}
                          />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center justify-center gap-4 mt-2">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-sm bg-primary-600"></div>
                      <span className="text-xs text-gray-500">Total Calls</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-sm bg-green-500"></div>
                      <span className="text-xs text-gray-500">Connected</span>
                    </div>
                    {avgCallDuration > 0 && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-sm bg-yellow-500"></div>
                        <span className="text-xs text-gray-500">Est. Talk Time (min)</span>
                      </div>
                    )}
                  </div>
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-100">
                    <div className="text-center">
                      <div className="text-sm font-bold text-gray-900">
                        {userReport.hourlyBreakdown.reduce((sum, h) => sum + h.total, 0)}
                      </div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-green-600">
                        {userReport.hourlyBreakdown.reduce((sum, h) => sum + h.connected, 0)}
                      </div>
                      <div className="text-xs text-gray-500">Connected</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-primary-600">
                        {(() => {
                          const total = userReport.hourlyBreakdown.reduce((sum, h) => sum + h.total, 0);
                          const connected = userReport.hourlyBreakdown.reduce((sum, h) => sum + h.connected, 0);
                          return total > 0 ? Math.round((connected / total) * 100) + '%' : '0%';
                        })()}
                      </div>
                      <div className="text-xs text-gray-500">Rate</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Calls */}
              {userReport.callLogs && userReport.callLogs.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 sm:mb-3">Recent Calls ({userReport.callLogs.length})</h4>
                  <div className="max-h-40 sm:max-h-48 overflow-y-auto space-y-2">
                    {userReport.callLogs.slice(0, 10).map((call, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 sm:p-2.5 bg-gray-50 rounded-lg text-xs sm:text-sm">
                        <div className="min-w-0">
                          <span className="font-medium truncate">{call.leadId?.name || call.phoneNumber}</span>
                          <span className="text-xs text-gray-400 ml-1 sm:ml-2">
                            {format(new Date(call.createdAt), 'h:mm a')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {call.duration > 0 && (
                            <span className="text-xs text-gray-500">
                              {formatAvgDuration(call.duration)}
                            </span>
                          )}
                          <Badge variant={call.status === 'connected' ? 'success' : 'danger'} size="sm">
                            {formatLabel(call.status)}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leads Worked */}
              {userReport.leadsWorked && userReport.leadsWorked.leads && userReport.leadsWorked.leads.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 sm:mb-3">Leads Worked ({userReport.leadsWorked.total})</h4>
                  <div className="max-h-40 sm:max-h-48 overflow-y-auto space-y-2">
                    {userReport.leadsWorked.leads.slice(0, 10).map((lead, idx) => {
                      // A lead in the "converted" pipeline stage shows as Converted
                      const effectiveStatus = lead.stage === 'converted' ? 'converted' : lead.status;
                      const statusColor = getLeadStatusColor(effectiveStatus);
                      const statusName = getLeadStatusName(effectiveStatus);
                      return (
                        <div key={idx} className="flex items-center justify-between p-2 sm:p-2.5 bg-gray-50 rounded-lg text-xs sm:text-sm">
                          <div className="min-w-0">
                            <span className="font-medium truncate">{lead.name}</span>
                            <span className="text-xs text-gray-400 ml-1 sm:ml-2">{lead.phone}</span>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${statusName === 'Hot' ? 'bg-red-100 text-red-600' : statusName === 'Warm' ? 'bg-yellow-100 text-yellow-600' : statusName === 'Cold' ? 'bg-blue-100 text-blue-600' : statusName === 'Converted' ? 'bg-green-100 text-green-600' : statusName === 'Closed' ? 'bg-gray-100 text-gray-600' : 'bg-gray-200 text-gray-700'}`}
                          >
                            {statusName}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowUserModal(false);
                    setSelectedUserId(null);
                  }}
                >
                  Close
                </Button>
                {/* <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigate(`/app/users/${userReport.user?._id}`);
                    setShowUserModal(false);
                  }}
                >
                  View Profile
                </Button> */}
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No data available</p>
          )}
        </Modal>
      </div>
    </Layout>
  );
};

export default DailyWorkReports;